import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAdminTranslation } from '../utils/useAdminTranslation';
import { translateCategories, translateCategory, translateCountry, languageNames, availableLanguages } from '../utils/translations';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { HeroSection } from '../components/home/HeroSection'; // Updated v2
import { ProductCard } from '../components/catalog/ProductCard';
import { ProductTable } from '../components/catalog/ProductTable';
import { ProductDetailDialog } from '../components/catalog/ProductDetailDialog';
import { PaginationControls } from '../components/catalog/PaginationControls';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger,
  SheetHeader,
  SheetTitle 
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Filter, 
  LayoutGrid, 
  List, 
  SlidersHorizontal, 
  Loader2,
  Search,
  X,
  Globe,
  FolderOpen,
  RotateCcw,
  TrendingUp,
  AlertTriangle,
  Download,
  DollarSign,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const HomePage = () => {
  const { language, tx } = useAdminTranslation();
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // Changed default to table
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Filter options (loaded from DB)
  const [uniqueCountries, setUniqueCountries] = useState([]);
  const [uniqueCategories, setUniqueCategories] = useState([]);

  // Active filters
  const [filters, setFilters] = useState({
    search: '',
    countries: [],  // Changed to array for multi-select
    categories: [], // Changed to array for multi-select
    casino: false,
    cbd: false,
    gambling: false,
    cryptocurrency: false,
    sex: false,
    erotic: false,
    vpn: false,
    tarot: false,
    porn: false,
    cannabis: false,
    dr_min: '',
    dr_max: '',
    traffic_min: '',
    traffic_max: '',
    price_min: '',
    price_max: '',
  });

  // Expanded accordion sections
  const [expandedSections, setExpandedSections] = useState(['search', 'country', 'category', 'dr', 'traffic', 'price', 'sensitive']);

  const texts = {
    es: {
      catalog: 'Catálogo', results: 'resultados', search: 'Buscar por dominio...', searchLabel: 'Buscar',
      country: 'País', allCountries: 'Todos los países', category: 'Categoría', allCategories: 'Todas las categorías',
      sensitiveTopics: 'Temáticas Sensibles', searchCountry: 'Buscar país...', searchCategory: 'Buscar categoría...',
      resetFilters: 'Resetear Todo', casino: 'Casino', cbd: 'CBD', gambling: 'Apuestas', cryptocurrency: 'Criptomonedas',
      sex: 'Sexo', erotic: 'Erótico', vpn: 'VPN', tarot: 'Tarot', porn: 'Porno', cannabis: 'Cannabis',
      filters: 'Filtros', clearFilters: 'Limpiar Filtros', applyFilters: 'Aplicar filtros',
      noResults: 'No se encontraron resultados', tryOther: 'Intenta con otros filtros',
      page: 'Página', of: 'de', showing: 'Mostrando', activeFilters: 'filtros activos',
      dr: 'Domain Rating (DR)', drMin: 'DR mínimo', drMax: 'DR máximo',
      traffic: 'Tráfico Orgánico', trafficMin: 'Tráfico mínimo', trafficMax: 'Tráfico máximo',
      tableUrl: 'URL', tableLanguage: 'Idioma', tableCountry: 'País', tableSiteType: 'Tipo Medio',
      tableCategory: 'Categoría', tableLinkType: 'Tipo Enlace', tableTag: 'Tag', tableRd: 'RD',
      tableTraffic: 'Tráfico', tablePrice: 'Precio', tableDr: 'DR', tablePriceSensitive: 'Precio Sensible',
      tableMoreInfo: 'Más Info', tableSimilarSites: 'Sitios Similares', moreInfoTitle: 'Información del Sitio',
      linkDuration: 'Duración Enlace', noSensitivePrice: 'NO', backToStart: 'Volver al Inicio',
      filtersActive: 'Filtros activos', itemsPerPage: 'por página', showingItems: 'Mostrando',
      countries: 'países', categories: 'categorías', rangeInfo: 'Rango de 0 a 100',
      monthlyVisits: 'Visitas mensuales estimadas', minPrice: 'Precio mínimo', maxPrice: 'Precio máximo',
      priceRange: 'Rango de Precio', price: 'Precio',
      cookieTitle: 'Aviso de Cookies', 
      cookieMessage: 'Utilizamos cookies para mejorar tu experiencia en nuestro sitio web. Al continuar navegando, aceptas el uso de cookies.',
      cookieMoreInfo: 'Más información sobre cookies',
      cookieAccept: 'Aceptar y Continuar',
      cookieReject: 'Rechazar y Salir',
    },
    en: {
      catalog: 'Catalog', results: 'results', search: 'Search by domain...', searchLabel: 'Search',
      country: 'Country', allCountries: 'All countries', category: 'Category', allCategories: 'All categories',
      sensitiveTopics: 'Sensitive Topics', searchCountry: 'Search country...', searchCategory: 'Search category...',
      resetFilters: 'Reset All', casino: 'Casino', cbd: 'CBD', gambling: 'Gambling', cryptocurrency: 'Cryptocurrency',
      sex: 'Sex', erotic: 'Erotic', vpn: 'VPN', tarot: 'Tarot', porn: 'Porn', cannabis: 'Cannabis',
      filters: 'Filters', clearFilters: 'Clear Filters', applyFilters: 'Apply filters',
      noResults: 'No results found', tryOther: 'Try different filters',
      page: 'Page', of: 'of', showing: 'Showing', activeFilters: 'active filters',
      dr: 'Domain Rating (DR)', drMin: 'Min DR', drMax: 'Max DR',
      traffic: 'Organic Traffic', trafficMin: 'Min traffic', trafficMax: 'Max traffic',
      tableUrl: 'URL', tableLanguage: 'Language', tableCountry: 'Country', tableSiteType: 'Site Type',
      tableCategory: 'Category', tableLinkType: 'Link Type', tableTag: 'Tag', tableRd: 'RD',
      tableTraffic: 'Traffic', tablePrice: 'Price', tableDr: 'DR', tablePriceSensitive: 'Sensitive Price',
      tableMoreInfo: 'More Info', tableSimilarSites: 'Similar Sites', moreInfoTitle: 'Site Information',
      linkDuration: 'Link Duration', noSensitivePrice: 'NO', backToStart: 'Back to Start',
      filtersActive: 'Active filters', itemsPerPage: 'per page', showingItems: 'Showing',
      countries: 'countries', categories: 'categories', rangeInfo: 'Range from 0 to 100',
      monthlyVisits: 'Estimated monthly visits', minPrice: 'Min price', maxPrice: 'Max price',
      priceRange: 'Price Range', price: 'Price',
      cookieTitle: 'Cookie Notice',
      cookieMessage: 'We use cookies to improve your experience on our website. By continuing to browse, you accept the use of cookies.',
      cookieMoreInfo: 'More information about cookies',
      cookieAccept: 'Accept and Continue',
      cookieReject: 'Reject and Leave',
    },
    de: {
      catalog: 'Katalog', results: 'Ergebnisse', search: 'Nach Domain suchen...', searchLabel: 'Suchen',
      country: 'Land', allCountries: 'Alle Länder', category: 'Kategorie', allCategories: 'Alle Kategorien',
      sensitiveTopics: 'Sensible Themen', searchCountry: 'Land suchen...', searchCategory: 'Kategorie suchen...',
      resetFilters: 'Alles zurücksetzen', casino: 'Casino', cbd: 'CBD', gambling: 'Glücksspiel', cryptocurrency: 'Kryptowährung',
      sex: 'Sex', erotic: 'Erotik', vpn: 'VPN', tarot: 'Tarot', porn: 'Porno', cannabis: 'Cannabis',
      filters: 'Filter', clearFilters: 'Filter löschen', applyFilters: 'Filter anwenden',
      noResults: 'Keine Ergebnisse gefunden', tryOther: 'Versuchen Sie andere Filter',
      page: 'Seite', of: 'von', showing: 'Zeige', activeFilters: 'aktive Filter',
      dr: 'Domain Rating (DR)', drMin: 'Min DR', drMax: 'Max DR',
      traffic: 'Organischer Traffic', trafficMin: 'Min Traffic', trafficMax: 'Max Traffic',
      tableUrl: 'URL', tableLanguage: 'Sprache', tableCountry: 'Land', tableSiteType: 'Medientyp',
      tableCategory: 'Kategorie', tableLinkType: 'Linktyp', tableTag: 'Tag', tableRd: 'RD',
      tableTraffic: 'Traffic', tablePrice: 'Preis', tableDr: 'DR', tablePriceSensitive: 'Sensibler Preis',
      tableMoreInfo: 'Mehr Info', tableSimilarSites: 'Ähnliche Seiten', moreInfoTitle: 'Website-Informationen',
      linkDuration: 'Linkdauer', noSensitivePrice: 'NEIN', backToStart: 'Zurück zum Anfang',
      filtersActive: 'Aktive Filter', itemsPerPage: 'pro Seite', showingItems: 'Zeige',
      countries: 'Länder', categories: 'Kategorien', rangeInfo: 'Bereich von 0 bis 100',
      monthlyVisits: 'Geschätzte monatliche Besuche', minPrice: 'Mindestpreis', maxPrice: 'Höchstpreis',
      priceRange: 'Preisbereich', price: 'Preis',
      cookieTitle: 'Cookie-Hinweis',
      cookieMessage: 'Wir verwenden Cookies, um Ihre Erfahrung auf unserer Website zu verbessern. Wenn Sie weiter surfen, akzeptieren Sie die Verwendung von Cookies.',
      cookieAccept: 'Akzeptieren und Fortfahren',
      cookieReject: 'Ablehnen und Verlassen',
      cookieMoreInfo: 'Weitere Informationen zu Cookies',
    },
    fr: {
      catalog: 'Catalogue', results: 'résultats', search: 'Rechercher par domaine...', searchLabel: 'Rechercher',
      country: 'Pays', allCountries: 'Tous les pays', category: 'Catégorie', allCategories: 'Toutes les catégories',
      sensitiveTopics: 'Sujets Sensibles', searchCountry: 'Rechercher un pays...', searchCategory: 'Rechercher une catégorie...',
      resetFilters: 'Tout réinitialiser', casino: 'Casino', cbd: 'CBD', gambling: 'Jeux d\'argent', cryptocurrency: 'Cryptomonnaie',
      sex: 'Sexe', erotic: 'Érotique', vpn: 'VPN', tarot: 'Tarot', porn: 'Porno', cannabis: 'Cannabis',
      filters: 'Filtres', clearFilters: 'Effacer les filtres', applyFilters: 'Appliquer les filtres',
      noResults: 'Aucun résultat trouvé', tryOther: 'Essayez d\'autres filtres',
      page: 'Page', of: 'de', showing: 'Affichage', activeFilters: 'filtres actifs',
      dr: 'Domain Rating (DR)', drMin: 'DR min', drMax: 'DR max',
      traffic: 'Trafic Organique', trafficMin: 'Trafic min', trafficMax: 'Trafic max',
      tableUrl: 'URL', tableLanguage: 'Langue', tableCountry: 'Pays', tableSiteType: 'Type de Site',
      tableCategory: 'Catégorie', tableLinkType: 'Type de Lien', tableTag: 'Tag', tableRd: 'RD',
      tableTraffic: 'Trafic', tablePrice: 'Prix', tableDr: 'DR', tablePriceSensitive: 'Prix Sensible',
      tableMoreInfo: 'Plus d\'Info', tableSimilarSites: 'Sites Similaires', moreInfoTitle: 'Informations du Site',
      linkDuration: 'Durée du Lien', noSensitivePrice: 'NON', backToStart: 'Retour au Début',
      filtersActive: 'Filtres actifs', itemsPerPage: 'par page', showingItems: 'Affichage',
      countries: 'pays', categories: 'catégories', rangeInfo: 'Plage de 0 à 100',
      monthlyVisits: 'Visites mensuelles estimées', minPrice: 'Prix min', maxPrice: 'Prix max',
      priceRange: 'Gamme de Prix', price: 'Prix',
      cookieTitle: 'Avis sur les Cookies',
      cookieMessage: 'Nous utilisons des cookies pour améliorer votre expérience sur notre site. En continuant à naviguer, vous acceptez l\'utilisation des cookies.',
      cookieAccept: 'Accepter et Continuer',
      cookieReject: 'Refuser et Quitter',
      cookieMoreInfo: 'Plus d\'informations sur les cookies',
    },
    it: {
      catalog: 'Catalogo', results: 'risultati', search: 'Cerca per dominio...', searchLabel: 'Cerca',
      country: 'Paese', allCountries: 'Tutti i paesi', category: 'Categoria', allCategories: 'Tutte le categorie',
      sensitiveTopics: 'Argomenti Sensibili', searchCountry: 'Cerca paese...', searchCategory: 'Cerca categoria...',
      resetFilters: 'Reimposta Tutto', casino: 'Casinò', cbd: 'CBD', gambling: 'Gioco d\'azzardo', cryptocurrency: 'Criptovaluta',
      sex: 'Sesso', erotic: 'Erotico', vpn: 'VPN', tarot: 'Tarocchi', porn: 'Porno', cannabis: 'Cannabis',
      filters: 'Filtri', clearFilters: 'Cancella filtri', applyFilters: 'Applica filtri',
      noResults: 'Nessun risultato trovato', tryOther: 'Prova altri filtri',
      page: 'Pagina', of: 'di', showing: 'Visualizzazione', activeFilters: 'filtri attivi',
      dr: 'Domain Rating (DR)', drMin: 'DR min', drMax: 'DR max',
      traffic: 'Traffico Organico', trafficMin: 'Traffico min', trafficMax: 'Traffico max',
      tableUrl: 'URL', tableLanguage: 'Lingua', tableCountry: 'Paese', tableSiteType: 'Tipo di Sito',
      tableCategory: 'Categoria', tableLinkType: 'Tipo di Link', tableTag: 'Tag', tableRd: 'RD',
      tableTraffic: 'Traffico', tablePrice: 'Prezzo', tableDr: 'DR', tablePriceSensitive: 'Prezzo Sensibile',
      tableMoreInfo: 'Più Info', tableSimilarSites: 'Siti Simili', moreInfoTitle: 'Informazioni del Sito',
      linkDuration: 'Durata del Link', noSensitivePrice: 'NO', backToStart: 'Torna all\'Inizio',
      filtersActive: 'Filtri attivi', itemsPerPage: 'per pagina', showingItems: 'Visualizzazione',
      countries: 'paesi', categories: 'categorie', rangeInfo: 'Intervallo da 0 a 100',
      monthlyVisits: 'Visite mensili stimate', minPrice: 'Prezzo min', maxPrice: 'Prezzo max',
      priceRange: 'Fascia di Prezzo', price: 'Prezzo',
      cookieTitle: 'Avviso sui Cookie',
      cookieMessage: 'Utilizziamo i cookie per migliorare la tua esperienza sul nostro sito. Continuando a navigare, accetti l\'uso dei cookie.',
      cookieAccept: 'Accetta e Continua',
      cookieReject: 'Rifiuta ed Esci',
      cookieMoreInfo: 'Maggiori informazioni sui cookie',
    }
  };

  const t = texts[language] || texts.es;

  // Fetch filter options on mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Fetch products when filters change
  useEffect(() => {
    fetchProducts(1);
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/filters/options`);
      setUniqueCountries(response.data.countries || []);
      setUniqueCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchProducts = async (page = 1, customFilters = null, customLimit = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', customLimit || itemsPerPage);
      
      // Use custom filters if provided, otherwise use state filters
      const f = customFilters || filters;
      
      if (f.search) params.append('search', f.search);
      // Handle multiple countries
      if (f.countries && f.countries.length > 0) {
        params.append('countries', f.countries.join(','));
      }
      // Handle multiple categories
      if (f.categories && f.categories.length > 0) {
        params.append('categories', f.categories.join(','));
      }
      if (f.casino) params.append('casino', 'true');
      if (f.cbd) params.append('cbd', 'true');
      if (f.gambling) params.append('gambling', 'true');
      if (f.cryptocurrency) params.append('cryptocurrency', 'true');
      if (f.sex) params.append('sex', 'true');
      if (f.erotic) params.append('erotic', 'true');
      if (f.vpn) params.append('vpn', 'true');
      if (f.tarot) params.append('tarot', 'true');
      if (f.porn) params.append('porn', 'true');
      if (f.cannabis) params.append('cannabis', 'true');
      if (f.dr_min) params.append('dr_min', f.dr_min);
      if (f.dr_max) params.append('dr_max', f.dr_max);
      if (f.traffic_min) params.append('traffic_min', f.traffic_min);
      if (f.traffic_max) params.append('traffic_max', f.traffic_max);
      if (f.price_min) params.append('price_min', f.price_min);
      if (f.price_max) params.append('price_max', f.price_max);
      if (sortField) {
        params.append('sort_by', sortField);
        params.append('sort_order', sortOrder);
      }

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data.products);
      setPagination(response.data.pagination);
      setSelectedProducts(new Set());
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Refetch when sort changes
  useEffect(() => {
    if (sortField) {
      fetchProducts(pagination.page);
    }
  }, [sortField, sortOrder]);

  const handleApplyFilters = () => {
    fetchProducts(1);
    setMobileFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      countries: [],
      categories: [],
      casino: false,
      cbd: false,
      gambling: false,
      cryptocurrency: false,
      sex: false,
      erotic: false,
      vpn: false,
      tarot: false,
      porn: false,
      cannabis: false,
      dr_min: '',
      dr_max: '',
      traffic_min: '',
      traffic_max: '',
      price_min: '',
      price_max: '',
    });
    // Fetch with cleared filters
    setTimeout(() => fetchProducts(1), 0);
  };

  const handlePageChange = (newPage) => {
    fetchProducts(newPage);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    fetchProducts(1, null, newLimit);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Open product detail dialog
  const openProductDialog = (product) => {
    setSelectedProduct(product);
    setProductDialogOpen(true);
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters.search !== '' ||
      filters.country !== '' ||
      filters.category !== '' ||
      filters.casino || filters.cbd || filters.gambling ||
      filters.cryptocurrency || filters.sex || filters.erotic ||
      filters.vpn || filters.tarot || filters.porn || filters.cannabis ||
      filters.dr_min !== '' || filters.dr_max !== '' ||
      filters.traffic_min !== '' || filters.traffic_max !== '';
  };

  // Clear all filters and go back to start
  const clearAllFilters = () => {
    const emptyFilters = {
      search: '',
      country: '',
      category: '',
      casino: false,
      cbd: false,
      gambling: false,
      cryptocurrency: false,
      sex: false,
      erotic: false,
      vpn: false,
      tarot: false,
      porn: false,
      cannabis: false,
      dr_min: '',
      dr_max: '',
      traffic_min: '',
      traffic_max: '',
    };
    setFilters(emptyFilters);
    fetchProducts(1, emptyFilters);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Download filtered results as Excel
  const [downloading, setDownloading] = useState(false);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [downloadingSelected, setDownloadingSelected] = useState(false);

  const handleSelectProduct = (productId) => {
    const ns = new Set(selectedProducts);
    if (ns.has(productId)) ns.delete(productId);
    else ns.add(productId);
    setSelectedProducts(ns);
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedProducts.size === 0) return;
    setDownloadingSelected(true);
    try {
      const response = await axios.post(`${API}/products/export-selected?lang=${encodeURIComponent(language || 'es')}`,
        { product_ids: Array.from(selectedProducts) },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `medios_seleccionados_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(tx(`${selectedProducts.size} medios descargados`, `${selectedProducts.size} media downloaded`));
    } catch (error) {
      console.error('Error downloading selected:', error);
    } finally {
      setDownloadingSelected(false);
    }
  };
  
  const handleDownloadFiltered = async () => {
    setShowDownloadConfirm(false);
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.country && filters.country !== 'all') params.append('country', filters.country);
      if (filters.category && filters.category !== 'all') params.append('category', filters.category);
      if (filters.dr_min) params.append('dr_min', filters.dr_min);
      if (filters.dr_max) params.append('dr_max', filters.dr_max);
      if (filters.traffic_min) params.append('traffic_min', filters.traffic_min);
      if (filters.traffic_max) params.append('traffic_max', filters.traffic_max);
      
      // Sensitive topics
      const sensitiveTopics = ['casino', 'cbd', 'gambling', 'cryptocurrency', 'sex', 'erotic', 'vpn', 'tarot', 'porn', 'cannabis'];
      sensitiveTopics.forEach(topic => {
        if (filters[topic]) params.append(topic, 'true');
      });
      // Client language (from website language selector)
      params.append('lang', language || 'es');

      const response = await axios.get(`${API}/products/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `catalogo_filtrado_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading:', error);
    } finally {
      setDownloading(false);
    }
  };

  // Find similar sites based on product attributes
  const findSimilarSites = (product) => {
    const drRange = 15; // +/- 15 DR
    
    // Reset filters first, then apply similar site criteria
    const newFilters = {
      search: '',
      country: product.country || '',
      category: '',
      casino: false,
      cbd: false,
      gambling: false,
      cryptocurrency: false,
      sex: false,
      erotic: false,
      vpn: false,
      tarot: false,
      porn: false,
      cannabis: false,
      dr_min: Math.max(0, (product.dr || 0) - drRange).toString(),
      dr_max: ((product.dr || 0) + drRange).toString(),
      traffic_min: '',
      traffic_max: '',
    };
    
    setFilters(newFilters);
    setProductDialogOpen(false);
    
    // Fetch with new filters
    setTimeout(() => {
      fetchProducts(1, newFilters);
    }, 100);
  };

  // Count active filters
  const activeFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.countries && filters.countries.length > 0) count += filters.countries.length;
    if (filters.categories && filters.categories.length > 0) count += filters.categories.length;
    if (filters.casino) count++;
    if (filters.cbd) count++;
    if (filters.gambling) count++;
    if (filters.cryptocurrency) count++;
    if (filters.sex) count++;
    if (filters.erotic) count++;
    if (filters.vpn) count++;
    if (filters.tarot) count++;
    if (filters.porn) count++;
    if (filters.cannabis) count++;
    if (filters.dr_min || filters.dr_max) count++;
    if (filters.traffic_min || filters.traffic_max) count++;
    return count;
  };

  // Count sensitive filters
  const sensitiveFiltesCount = () => {
    return [filters.casino, filters.cbd, filters.gambling, filters.cryptocurrency, 
            filters.sex, filters.erotic, filters.vpn, filters.tarot, 
            filters.porn, filters.cannabis].filter(Boolean).length;
  };

  // Filter sidebar content component - COMPACT VERSION
  const FilterContent = ({ onApply }) => {
    const [localSearch, setLocalSearch] = useState(filters.search);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [localDrMin, setLocalDrMin] = useState(filters.dr_min);
    const [localDrMax, setLocalDrMax] = useState(filters.dr_max);
    const [localTrafficMin, setLocalTrafficMin] = useState(filters.traffic_min);
    const [localTrafficMax, setLocalTrafficMax] = useState(filters.traffic_max);
    const [localPriceMin, setLocalPriceMin] = useState(filters.price_min);
    const [localPriceMax, setLocalPriceMax] = useState(filters.price_max);
    const [showSensitive, setShowSensitive] = useState(false);
    
    const [localCasino, setLocalCasino] = useState(filters.casino);
    const [localCbd, setLocalCbd] = useState(filters.cbd);
    const [localGambling, setLocalGambling] = useState(filters.gambling);
    const [localCryptocurrency, setLocalCryptocurrency] = useState(filters.cryptocurrency);
    const [localSex, setLocalSex] = useState(filters.sex);
    const [localErotic, setLocalErotic] = useState(filters.erotic);
    const [localVpn, setLocalVpn] = useState(filters.vpn);
    const [localTarot, setLocalTarot] = useState(filters.tarot);
    const [localPorn, setLocalPorn] = useState(filters.porn);
    const [localCannabis, setLocalCannabis] = useState(filters.cannabis);
    
    const localSensitiveCount = () => {
      return [localCasino, localCbd, localGambling, localCryptocurrency, 
              localSex, localErotic, localVpn, localTarot, 
              localPorn, localCannabis].filter(Boolean).length;
    };
    
    const handleApplyWithSync = () => {
      const newFilters = {
        ...filters,
        search: localSearch,
        dr_min: localDrMin,
        dr_max: localDrMax,
        traffic_min: localTrafficMin,
        traffic_max: localTrafficMax,
        price_min: localPriceMin,
        price_max: localPriceMax,
        casino: localCasino,
        cbd: localCbd,
        gambling: localGambling,
        cryptocurrency: localCryptocurrency,
        sex: localSex,
        erotic: localErotic,
        vpn: localVpn,
        tarot: localTarot,
        porn: localPorn,
        cannabis: localCannabis,
      };
      setFilters(newFilters);
      fetchProducts(1, newFilters);
      setMobileFiltersOpen(false);
    };
    
    const handleCountryToggle = (country) => {
      const currentCountries = filters.countries || [];
      const newCountries = currentCountries.includes(country)
        ? currentCountries.filter(c => c !== country)
        : [...currentCountries, country];
      const newFilters = { ...filters, countries: newCountries };
      setFilters(newFilters);
    };
    
    const handleCategoryToggle = (category) => {
      const currentCategories = filters.categories || [];
      const newCategories = currentCategories.includes(category)
        ? currentCategories.filter(c => c !== category)
        : [...currentCategories, category];
      const newFilters = { ...filters, categories: newCategories };
      setFilters(newFilters);
    };
    
    const handleReset = () => {
      setLocalSearch('');
      setLocalDrMin('');
      setLocalDrMax('');
      setLocalTrafficMin('');
      setLocalTrafficMax('');
      setLocalPriceMin('');
      setLocalPriceMax('');
      setLocalCasino(false);
      setLocalCbd(false);
      setLocalGambling(false);
      setLocalCryptocurrency(false);
      setLocalSex(false);
      setLocalErotic(false);
      setLocalVpn(false);
      setLocalTarot(false);
      setLocalPorn(false);
      setLocalCannabis(false);
      handleClearFilters();
    };
    
    return (
      <div className="space-y-3">
        {/* Row 1: Search, Country, Category */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{t.searchLabel}</Label>
            <Input
              type="text"
              placeholder={t.search}
              value={localSearch}
              onChange={(e) => {
                // Clean URL: remove protocol, www., and trailing slash
                let value = e.target.value
                  .replace(/^https?:\/\//i, '')
                  .replace(/^www\./i, '')
                  .replace(/\/+$/, '')
                  .trim();
                setLocalSearch(value);
                
                // Auto-search with debounce
                if (searchTimeout) clearTimeout(searchTimeout);
                const timeout = setTimeout(() => {
                  const newFilters = { ...filters, search: value };
                  setFilters(newFilters);
                  fetchProducts(1, newFilters);
                }, 500);
                setSearchTimeout(timeout);
              }}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">
              {t.country} {filters.countries?.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{filters.countries.length}</Badge>}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-8 text-sm w-full justify-between font-normal">
                  {filters.countries?.length > 0 
                    ? `${filters.countries.length} ${tx('seleccionados', 'selected')}`
                    : t.allCountries}
                  <ChevronDown className="h-3 w-3 ml-1 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-2" align="start">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium">{t.country}</span>
                  {filters.countries?.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={() => setFilters({...filters, countries: []})}
                    >
                      {tx('Limpiar', 'Clear')}
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-1">
                    {uniqueCountries.map((country) => (
                      <label 
                        key={country} 
                        htmlFor={`catalog-country-${country}`}
                        className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                      >
                        <Checkbox 
                          id={`catalog-country-${country}`}
                          checked={filters.countries?.includes(country)} 
                          onCheckedChange={() => handleCountryToggle(country)}
                        />
                        <span className="text-sm">{translateCountry(country, language)}</span>
                      </label>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">
              {t.category} {filters.categories?.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{filters.categories.length}</Badge>}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-8 text-sm w-full justify-between font-normal">
                  {filters.categories?.length > 0 
                    ? `${filters.categories.length} ${tx('seleccionadas', 'selected')}`
                    : t.allCategories}
                  <ChevronDown className="h-3 w-3 ml-1 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-2" align="start">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium">{t.category}</span>
                  {filters.categories?.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={() => setFilters({...filters, categories: []})}
                    >
                      {tx('Limpiar', 'Clear')}
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-1">
                    {uniqueCategories.map((category) => (
                      <label 
                        key={category} 
                        htmlFor={`catalog-category-${category}`}
                        className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                      >
                        <Checkbox 
                          id={`catalog-category-${category}`}
                          checked={filters.categories?.includes(category)} 
                          onCheckedChange={() => handleCategoryToggle(category)}
                        />
                        <span className="text-sm">{translateCategory(category, language)}</span>
                      </label>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex flex-col gap-1 items-end">
            <Button onClick={handleReset} size="sm" className="h-8 px-4 bg-emerald-500 hover:bg-emerald-600 text-white">
              <RotateCcw className="h-3 w-3 mr-1" />
              {t.clearFilters}
            </Button>
            <Button onClick={handleApplyWithSync} size="sm" className="h-10 px-8 bg-rose-500 hover:bg-rose-600 text-white">
              <Search className="h-4 w-4 mr-2" />
              {tx('Buscar', 'Search')}
            </Button>
          </div>
        </div>
        
        {/* Row 2: Numeric filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">DR Min</Label>
            <Input
              type="number"
              min="0"
              max="100"
              placeholder="0"
              value={localDrMin}
              onChange={(e) => setLocalDrMin(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">DR Max</Label>
            <Input
              type="number"
              min="0"
              max="100"
              placeholder="100"
              value={localDrMax}
              onChange={(e) => setLocalDrMax(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{tx('Tráfico Min', 'Traffic Min')}</Label>
            <Input
              type="number"
              min="0"
              placeholder="0"
              value={localTrafficMin}
              onChange={(e) => setLocalTrafficMin(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{tx('Tráfico Max', 'Traffic Max')}</Label>
            <Input
              type="number"
              min="0"
              placeholder="∞"
              value={localTrafficMax}
              onChange={(e) => setLocalTrafficMax(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{tx('Precio Min €', 'Price Min €')}</Label>
            <Input
              type="number"
              min="0"
              placeholder="0"
              value={localPriceMin}
              onChange={(e) => setLocalPriceMin(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{tx('Precio Max €', 'Price Max €')}</Label>
            <Input
              type="number"
              min="0"
              placeholder="∞"
              value={localPriceMax}
              onChange={(e) => setLocalPriceMax(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        </div>
        
        {/* Row 3: Sensitive topics toggle */}
        <div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowSensitive(!showSensitive)}
            className="h-7 text-xs"
          >
            <AlertTriangle className="h-3 w-3 mr-1 text-warning" />
            {t.sensitiveTopics}
            {localSensitiveCount() > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs h-4 px-1">{localSensitiveCount()}</Badge>
            )}
            <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${showSensitive ? 'rotate-180' : ''}`} />
          </Button>
          
          {showSensitive && (
            <div className="flex flex-wrap gap-2 mt-2 p-2 bg-muted/30 rounded-lg">
              {[
                { id: 'casino', label: t.casino, checked: localCasino, onChange: setLocalCasino },
                { id: 'gambling', label: t.gambling, checked: localGambling, onChange: setLocalGambling },
                { id: 'cryptocurrency', label: t.cryptocurrency, checked: localCryptocurrency, onChange: setLocalCryptocurrency },
                { id: 'cbd', label: t.cbd, checked: localCbd, onChange: setLocalCbd },
                { id: 'cannabis', label: t.cannabis, checked: localCannabis, onChange: setLocalCannabis },
                { id: 'vpn', label: t.vpn, checked: localVpn, onChange: setLocalVpn },
                { id: 'tarot', label: t.tarot, checked: localTarot, onChange: setLocalTarot },
                { id: 'erotic', label: t.erotic, checked: localErotic, onChange: setLocalErotic },
                { id: 'sex', label: t.sex, checked: localSex, onChange: setLocalSex },
                { id: 'porn', label: t.porn, checked: localPorn, onChange: setLocalPorn },
              ].map(({ id, label, checked, onChange }) => (
                <div key={id} className="flex items-center gap-1">
                  <Checkbox
                    id={`filter-${id}`}
                    checked={checked}
                    onCheckedChange={onChange}
                    className="h-3 w-3"
                  />
                  <Label htmlFor={`filter-${id}`} className="text-xs cursor-pointer">{label}</Label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <HeroSection productCount={pagination.total} />

        {/* Catalog Section */}
        <section id="catalog" className="py-12 lg:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="flex flex-col items-center gap-4 mb-6">
              <div className="text-center">
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
                  {t.catalog}
                </h2>
                <p className="text-muted-foreground mt-1">
                  {pagination.total.toLocaleString()} {t.results}
                </p>
              </div>
              
              {/* Download Button - Centered */}
              <Button
                onClick={() => setShowDownloadConfirm(true)}
                disabled={downloading || pagination.total === 0}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6 py-2"
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {tx('Descarga de Medios', 'Download Media')}
              </Button>
            </div>

            {/* Download Confirmation Dialog */}
            {showDownloadConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4 animate-in fade-in zoom-in duration-200">
                  <div className="text-center">
                    <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                      <Download className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {tx('Confirmar Descarga', 'Confirm Download')}
                    </h3>
                    <p className="text-gray-600 mb-2">
                      {tx('Se descargarán', 'Will download')} <span className="font-bold text-emerald-600">{pagination.total.toLocaleString()}</span> {tx('medios en formato Excel', 'media in Excel format')}
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                      {tx('Solo se incluirán los resultados según los filtros aplicados', 'Only results matching applied filters will be included')}
                    </p>
                    <div className="flex gap-3 justify-center">
                      <Button
                        variant="outline"
                        onClick={() => setShowDownloadConfirm(false)}
                        className="px-6"
                      >
                        {tx('Cancelar', 'Cancel')}
                      </Button>
                      <Button
                        onClick={handleDownloadFiltered}
                        className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {tx('Descargar', 'Download')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-3 flex-wrap mt-4">
                {/* Items per page selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground hidden sm:inline">{t.showingItems}</span>
                  <select 
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="border border-border rounded-md px-2 py-1 text-sm bg-background"
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                  </select>
                  <span className="text-sm text-muted-foreground">{t.itemsPerPage}</span>
                </div>

                <div className="flex items-center gap-3">
                {/* View Toggle */}
                <div className="flex items-center border border-border rounded-lg p-1">
                  <Button
                    variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                    className="h-8 w-8 p-0"
                    title="Vista Tabla"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className="h-8 w-8 p-0"
                    title="Vista Cuadrícula"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Main Content - Filters on top */}
            <div className="flex flex-col gap-4">
              {/* Horizontal Filters Panel */}
              <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                <div className="p-3 border-b border-border bg-muted/30">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <Filter className="h-4 w-4 text-primary" />
                      {t.filters}
                    </h3>
                    {activeFiltersCount() > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {activeFiltersCount()}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <FilterContent onApply={handleApplyFilters} />
                </div>
              </div>

              {/* Products Grid/Table */}
              <div className="flex-1 min-w-0">
                {loading && (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                )}
                
                {!loading && products.length > 0 && (
                  <>
                    {viewMode === 'table' ? (
                      <ProductTable 
                        products={products} 
                        texts={t} 
                        onProductClick={openProductDialog}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        selectedProducts={selectedProducts}
                        onSelectProduct={handleSelectProduct}
                        onSelectAll={handleSelectAll}
                      />
                    ) : (
                      /* GRID VIEW */
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                        {products.map((product) => (
                          <ProductCard key={product.id} product={product} />
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Pagination Controls */}
                {!loading && products.length > 0 && (
                  <PaginationControls 
                    pagination={pagination} 
                    onPageChange={handlePageChange} 
                  />
                )}

                {/* No Results */}
                {!loading && products.length === 0 && (
                  <div className="text-center py-16">
                    <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-muted mb-4">
                      <Filter className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {t.noResults}
                    </h3>
                    <p className="text-muted-foreground mb-4">{t.tryOther}</p>
                    <Button variant="outline" onClick={handleClearFilters}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      {t.clearFilters}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Floating bar for selected products */}
      {selectedProducts.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg p-3 flex items-center justify-center gap-4">
          <span className="text-sm font-medium">
            {selectedProducts.size} {tx('medios seleccionados', 'media selected')}
          </span>
          <Button
            onClick={handleDownloadSelected}
            disabled={downloadingSelected}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
            size="sm"
          >
            {downloadingSelected ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            {tx('Descargar Seleccionados', 'Download Selected')}
          </Button>
          <Button
            onClick={() => setSelectedProducts(new Set())}
            variant="outline"
            size="sm"
          >
            {tx('Limpiar selección', 'Clear selection')}
          </Button>
        </div>
      )}

      {/* Product Detail Dialog */}
      <ProductDetailDialog
        product={selectedProduct}
        open={productDialogOpen}
        onOpenChange={setProductDialogOpen}
        onFindSimilar={findSimilarSites}
        language={language}
        texts={t}
      />

      <Footer />
    </div>
  );
};

export default HomePage;
